import * as Sentry from '@sentry/react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { SocketProvider } from './contexts/SocketContext';
import { PollProvider } from './contexts/PollContext';
import { PlayerProvider } from './contexts/PlayerContext';
import GoogleSignIn from './components/GoogleSignIn';
import Lobby from './components/Lobby';
import GameRoom from './components/GameRoom';
import PollApp from './components/PollApp';
import GuessGameApp from './components/GuessGameApp';
import Leaderboard from './components/Leaderboard';
import GuessGameLeaderboard from './components/GuessGameLeaderboard';
import AllTimeLeaderboard from './components/AllTimeLeaderboard';
import PastGames from './components/PastGames';
import AdminDashboard from './components/admin/AdminDashboard';
import { ProtectedAdminRoute } from './components/ProtectedAdminRoute';
import { NotificationBanner } from './components/NotificationBanner';
import { NotificationPermissionPrompt } from './components/NotificationPermissionPrompt';
import { BrowserNotificationHandler } from './components/BrowserNotificationHandler';

function App() {
  return (
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => (
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0 w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h1 className="text-xl font-semibold text-gray-900">Something went wrong</h1>
            </div>
            <p className="text-gray-600">
              We've been notified and are working on a fix. Please try again.
            </p>
            {import.meta.env.DEV && (
              <details className="mt-4 p-3 bg-gray-50 rounded border border-gray-200">
                <summary className="cursor-pointer text-sm font-medium text-gray-700">
                  Error details
                </summary>
                <pre className="mt-2 text-xs text-red-600 overflow-auto">
                  {error.toString()}
                </pre>
              </details>
            )}
            <div className="flex gap-3">
              <button
                onClick={resetError}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium"
              >
                Try again
              </button>
              <button
                onClick={() => window.location.href = '/'}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors font-medium"
              >
                Go home
              </button>
            </div>
          </div>
        </div>
      )}
      showDialog
    >
      <div className="min-h-screen bg-background">
        <AuthProvider>
          <BrowserNotificationHandler>
            <NotificationBanner />
            <NotificationPermissionPrompt />
            <PlayerProvider>
              <SocketProvider>
                <PollProvider>
                  <Router>
                    <Routes>
                      <Route path="/" element={<GoogleSignIn />} />
                      <Route path="/lobby" element={<Lobby />} />
                      <Route path="/game/:roomId" element={<GameRoom />} />
                      <Route path="/poll/:roomId" element={<PollApp />} />
                      <Route path="/guess/:gameId" element={<GuessGameApp />} />
                      <Route path="/leaderboard/:roomId" element={<Leaderboard />} />
                      <Route path="/guess-leaderboard/:gameId" element={<GuessGameLeaderboard />} />
                      <Route path="/all-time-leaderboard" element={<AllTimeLeaderboard />} />
                      <Route path="/past-games" element={<PastGames />} />
                      <Route path="/admin" element={
                        <ProtectedAdminRoute>
                          <AdminDashboard />
                        </ProtectedAdminRoute>
                      } />
                    </Routes>
                  </Router>
                </PollProvider>
              </SocketProvider>
            </PlayerProvider>
          </BrowserNotificationHandler>
        </AuthProvider>
      </div>
    </Sentry.ErrorBoundary>
  );
}

export default App; 